import type { PublicFormVersion } from "@kushya/shared";

import { API_BASE_URL } from "./forms";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const extractErrorMessage = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }

  const error = payload.error;

  if (typeof error === "string") {
    return error;
  }

  if (isRecord(error) && typeof error.message === "string") {
    return error.message;
  }

  return undefined;
};

/**
 * Fetch a form version for preview using a preview token.
 * This allows viewing draft forms without publishing them.
 */
export async function fetchPreviewForm(
  token: string,
): Promise<PublicFormVersion> {
  const encodedToken = encodeURIComponent(token);
  const url = `${API_BASE_URL}/forms/public/preview?token=${encodedToken}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    let body: unknown;

    try {
      body = await response.json();
    } catch {
      body = undefined;
    }

    if (response.status === 401) {
      const message = extractErrorMessage(body);
      if (message?.includes("expired")) {
        throw new Error("Preview token has expired. Please refresh the preview.");
      }
      throw new Error("Invalid preview token");
    }

    if (response.status === 404) {
      throw new Error("Form draft not found");
    }

    const message = extractErrorMessage(body);
    throw new Error(message ?? "Failed to fetch form preview");
  }

  return (await response.json()) as PublicFormVersion;
}
